import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import { NetworkHealthService, NetworkHealthStatus, NetworkHealthConfig } from '../services/NetworkHealth/NetworkHealthService';
import { VpnLockOverlay } from '../components/VpnLockOverlay/VpnLockOverlay';

/**
 * Network health context value type
 */
export interface NetworkHealthContextValue {
    /** Current network health status */
    status: NetworkHealthStatus;
    /** Whether the network is connected */
    isConnected: boolean;
    /** Whether the network is disconnected */
    isDisconnected: boolean;
    /** Last error message if any */
    lastError: string | undefined;
    /** Manually mark as connected (e.g., after successful retry) */
    markConnected: () => void;
    /** Temporarily dismiss the overlay (useful for testing) */
    dismissOverlay: () => void;
}

const NetworkHealthContext = createContext<NetworkHealthContextValue | undefined>(undefined);

/**
 * Props for NetworkHealthProvider
 */
export interface NetworkHealthProviderProps {
    children: ReactNode;
    /** Optional configuration overrides */
    config?: Partial<NetworkHealthConfig>;
    /** Whether to show the lock overlay when disconnected (default: true) */
    showLockOverlay?: boolean;
    /** Custom title for the lock overlay */
    overlayTitle?: string;
    /** Custom description for the lock overlay */
    overlayDescription?: string;
}

/**
 * NetworkHealthProvider
 * 
 * Provides network health monitoring context and automatic VPN lock overlay.
 * Wrap your app with this provider to enable network health monitoring.
 * 
 * This provider detects VPN disconnection from API call failures:
 * - 403 Forbidden (Access Restrictions block)
 * - Network errors / timeouts (VPN disconnected)
 * 
 * Usage:
 * ```tsx
 * <NetworkHealthProvider>
 *     <App />
 * </NetworkHealthProvider>
 * ```
 * 
 * The provider will:
 * 1. Listen for API failures via NetworkHealthService
 * 2. Show a full-page overlay when VPN-related errors are detected
 * 3. Block all UI interactions until VPN is restored
 * 4. Hide overlay when API calls succeed again
 */
export const NetworkHealthProvider: React.FC<NetworkHealthProviderProps> = ({
    children,
    config,
    showLockOverlay = true,
    overlayTitle,
    overlayDescription
}) => {
    const [status, setStatus] = useState<NetworkHealthStatus>(NetworkHealthStatus.UNKNOWN);
    const [lastError, setLastError] = useState<string | undefined>();
    const [isDismissed, setIsDismissed] = useState(false);
    // Track if we're in a disconnected state that triggered the overlay
    const [isInDisconnectedState, setIsInDisconnectedState] = useState(false);
    // Track if user is retrying
    const [isRetrying, setIsRetrying] = useState(false);

    // Create stable service instance with config
    const service = useMemo(() => NetworkHealthService.getInstance(config), [config]);

    useEffect(() => {
        // Set initial status
        setStatus(service.getStatus());
        setLastError(service.getLastError());

        // Subscribe to status changes
        const handleStatusChange = (newStatus: NetworkHealthStatus, error?: string) => {
            setStatus(newStatus);
            setLastError(error);
            setIsRetrying(false);
            
            if (newStatus === NetworkHealthStatus.CONNECTED) {
                // Clear disconnected state - this hides the overlay
                setIsInDisconnectedState(false);
                setIsDismissed(false);
            } else if (newStatus === NetworkHealthStatus.DISCONNECTED) {
                // Enter disconnected state - show overlay and keep it visible
                setIsInDisconnectedState(true);
            }
        };

        service.addStatusListener(handleStatusChange);

        // No initial health check needed - status is determined by API call results

        // Cleanup on unmount
        return () => {
            service.removeStatusListener(handleStatusChange);
        };
    }, [service]);

    const markConnected = useCallback((): void => {
        service.markConnected();
    }, [service]);

    const dismissOverlay = useCallback((): void => {
        setIsDismissed(true);
    }, []);

    const handleRetry = useCallback(async (): Promise<void> => {
        setIsRetrying(true);
        // Trigger a page reload to retry all API calls
        window.location.reload();
    }, []);

    const contextValue: NetworkHealthContextValue = {
        status,
        isConnected: status === NetworkHealthStatus.CONNECTED,
        isDisconnected: status === NetworkHealthStatus.DISCONNECTED,
        lastError,
        markConnected,
        dismissOverlay
    };

    // Show overlay only when in disconnected state and not manually dismissed
    const shouldShowOverlay = showLockOverlay && 
                              !isDismissed &&
                              isInDisconnectedState;

    return (
        <NetworkHealthContext.Provider value={contextValue}>
            {children}
            <VpnLockOverlay
                isVisible={shouldShowOverlay}
                appName="CASE Portal"
                title={overlayTitle}
                description={overlayDescription}
            />
        </NetworkHealthContext.Provider>
    );
};

/**
 * Hook to access network health context
 * 
 * @throws Error if used outside of NetworkHealthProvider
 */
export const useNetworkHealthContext = (): NetworkHealthContextValue => {
    const context = useContext(NetworkHealthContext);
    
    if (context === undefined) {
        throw new Error('useNetworkHealthContext must be used within a NetworkHealthProvider');
    }
    
    return context;
};

export default NetworkHealthProvider;
